import {
  Button,
  ButtonGroup,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormSubmitHandler,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetServiceOrderIntegrationsMazdaClaimQuery,
  usePostServiceOrderIntegrationsMazdaClaimSendMutation,
  usePutServiceOrderIntegrationsMazdaClaimMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {TestIdProps, suffixTestId} from 'shared/web';

import {
  MAZDA_CLAIM_MAINTENANCE_DONE_RIGHT,
  MAZDA_CLAIM_MAINTENANCE_NOT_DONE_CORRECTLY,
  MazdaClaimSendFormSchema,
  MazdaClaimSendFormType,
} from '../types/MazdaClaimSendFormType';
import {mapMazdaClaimFormToRequestBody} from '../utils/mapMazdaClaimFormToRequestBody';
import {notifyMazdaClaimSendMessages} from '../utils/notifyMazdaClaimSendMessages';
import {toMazdaClaimMaintenanceAnswer} from '../utils/toMazdaClaimMaintenanceAnswer';
import {MazdaClaimCampaignFields} from './MazdaClaimCampaignFields';
import {MazdaClaimStandardFields} from './MazdaClaimStandardFields';

const MAINTENANCE_OPTIONS = [
  {
    value: MAZDA_CLAIM_MAINTENANCE_DONE_RIGHT,
    label: i18n.t('entity.orderIntegration.labels.maintenanceDoneRight'),
  },
  {
    value: MAZDA_CLAIM_MAINTENANCE_NOT_DONE_CORRECTLY,
    label: i18n.t('entity.orderIntegration.labels.maintenanceNotDoneCorrectly'),
  },
];

interface MazdaClaimSendDialogProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isPreauthorizationAvailable: boolean;
  onClose: () => void;
}

export function MazdaClaimSendDialog(props: MazdaClaimSendDialogProps) {
  const {
    data: claim,
    isLoading,
    isError,
  } = useGetServiceOrderIntegrationsMazdaClaimQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {refetchOnMountOrArgChange: true}
  );

  const [putMazdaClaim, {isLoading: isSaving}] = usePutServiceOrderIntegrationsMazdaClaimMutation();
  const [sendMazdaClaim, {isLoading: isSending}] =
    usePostServiceOrderIntegrationsMazdaClaimSendMutation();

  const isBusy = isSaving || isSending;

  const isCampaignClaim = isNotNilOrEmpty(claim?.campaignNumber);

  const claimValues = {
    campaignNumber: claim?.campaignNumber ?? undefined,
    campaignVariantNumber: claim?.campaignVariantNumber ?? undefined,
    scope: claim?.scope ?? undefined,
    warrantyType: claim?.warrantyType ?? undefined,
    mileage: claim?.mileage ?? undefined,
    repairDate: claim?.repairDate ?? undefined,
    vehicleServicedAccordingToManufacturerSpecs: toMazdaClaimMaintenanceAnswer(
      claim?.vehicleServicedAccordingToManufacturerSpecs
    ),
    installationDate: claim?.installationDate ?? undefined,
    installationMileage: claim?.installationMileage ?? undefined,
    installationRepairNo: claim?.installationRepairNo ?? undefined,
    isSpecialCausalPart: isNotNilOrEmpty(claim?.specialCausalPart),
    causalPart: claim?.causalPart ?? undefined,
    specialCausalPart: claim?.specialCausalPart ?? undefined,
    message: claim?.message ?? undefined,
  };

  const claimArgs = {
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  };

  const handleSend: FormSubmitHandler<MazdaClaimSendFormType> = async (data) =>
    await putMazdaClaim({...claimArgs, body: mapMazdaClaimFormToRequestBody(data)})
      .unwrap()
      .then(() => sendMazdaClaim(claimArgs).unwrap())
      .then((response) => {
        notifyMazdaClaimSendMessages(response);
        props.onClose();
      })
      .catch(handleApiError);

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
      <Form<MazdaClaimSendFormType>
        onSubmit={handleSend}
        defaultValues={claimValues}
        values={claimValues}
        resetOptions={{keepDirtyValues: true}}
        experimentalZodSchema={MazdaClaimSendFormSchema}
      >
        {(control, formApi) => {
          const handleSaveAndClose = () =>
            putMazdaClaim({
              ...claimArgs,
              body: mapMazdaClaimFormToRequestBody(formApi.getValues()),
            })
              .unwrap()
              .then(props.onClose)
              .catch(handleApiError);

          return (
            <VStack spacing={4}>
              {isCampaignClaim ? (
                <MazdaClaimCampaignFields
                  control={control}
                  maintenanceOptions={MAINTENANCE_OPTIONS}
                  data-testid={props['data-testid']}
                />
              ) : (
                <MazdaClaimStandardFields
                  control={control}
                  formApi={formApi}
                  maintenanceOptions={MAINTENANCE_OPTIONS}
                  serviceCaseId={props.serviceCaseId}
                  serviceOrderId={props.serviceOrderId}
                  isPreauthorizationAvailable={props.isPreauthorizationAvailable}
                  data-testid={props['data-testid']}
                />
              )}
              <DialogFooter>
                <ButtonGroup align="right">
                  <Button
                    title={i18n.t('general.actions.discard')}
                    variant="secondary"
                    onClick={props.onClose}
                    isDisabled={isBusy}
                    data-testid={suffixTestId('discard', props)}
                  />
                  <Button
                    title={i18n.t('general.actions.saveAndClose')}
                    variant="secondary"
                    onClick={handleSaveAndClose}
                    isLoading={isSaving}
                    isDisabled={isBusy}
                    data-testid={suffixTestId('saveAndClose', props)}
                  />
                  <FormButton
                    control={control}
                    title={i18n.t('entity.orderIntegration.actions.send')}
                    type="submit"
                    isLoading={isBusy}
                    isDisabled={isBusy}
                    data-testid={suffixTestId('send', props)}
                  />
                </ButtonGroup>
              </DialogFooter>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
